import { io } from 'socket.io-client';

import store from '../redux/store';
import { appendMessage, addContact } from '../redux/whatsappSlice';
import { updateAllUsers } from '../redux/usersSlice';
import { setOnlineUsers } from '../redux/onlineUsersSlice';
import {
	addFreshLead,
	markLeadClaimed,
	addApprovalLead,
	addAdminLeadAlert,
} from '../redux/freshLeadSlice';
import {
	addFreshLeadPool,
	markLeadDecisionFinalized,
} from '../redux/freshLeadPoolSlice';
import { updateCampaign } from '../redux/whatsappCampaignSlice';
import keys from 'config/keys';
import { addAnnouncement, addAnnouncements } from '../redux/announcementsSlice';
import { refreshPermissions } from '../redux/permissionSlice';
import { NOTIFICATION_TYPES } from 'constants/notification.contants';
import { triggerBrowserNotification } from './notification/browserNotification';
import { setNotifyItem } from '../redux/notificationSlice';
import { leadUrl } from 'views/notification/newLeadAdminNotification/leadUrl';

class SocketService {
	constructor() {
		this.socket = null;
		this.events = new Map();
		this.connectionStatus = 'disconnected';
		this.maxReconnectionAttempts = 10;
		this.connectionPromise = null;
	}

	/**
	 * Initialize socket connection
	 * @param {string} url - Socket server URL
	 * @param {object} [options] - Socket.IO options
	 * @returns {Promise} Resolves when connected
	 */
	connect(url = keys.socketIoUrl, options = {}) {
		if (this.connectionPromise) {
			return this.connectionPromise;
		}

		const tenantId = localStorage.getItem('tenantId') || null;
		// The token is what lets the server establish WHO this socket is, which
		// WhatsApp V2 requires before it will serve any event. A socket without
		// one still connects and still works on V1 — the server treats identity
		// as additive, so this cannot break an older client.
		const token =
			localStorage.getItem('accessToken') ||
			sessionStorage.getItem('accessToken') ||
			localStorage.getItem('token') ||
			sessionStorage.getItem('token') ||
			null;

		console.log('Socket Tenant ID: ', tenantId);

		if (!tenantId) {
			console.error('Tenant ID not found');
			return;
		}

		// Default options with merging
		// transports: ['socket.io'],
		const defaultOptions = {
			path: '/socket.io',
			reconnection: true,
			reconnectionAttempts: Infinity || this.maxReconnectionAttempts,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			autoConnect: true,
			forceNew: true,
			timeout: 5000,
			auth: {
				tenantId,
				token,
			},
		};

		this.socket = io(url, { ...defaultOptions, ...options });

		this.connectionPromise = new Promise((resolve, reject) => {
			this.socket.on('connect', () => {
				// console.log('Socket connected:', this.socket.id);
				this.connectionStatus = 'connected';
				// this.reconnectionAttempts = 0;

				// ★ REPLAY HANDLERS REGISTERED WHILE THERE WAS NO SOCKET.
				// `on()` always records a handler but can only bind it when a
				// socket exists, and `disconnect()` sets `this.socket = null`.
				// Any screen that disconnects therefore leaves every LATER
				// subscriber silently unbound until a full page reload — the Meta
				// WhatsApp inbox registers seven listeners and would receive
				// nothing at all. `_bindEvents` existed for exactly this and had
				// no caller anywhere in the app.
				this._bindEvents();

				resolve(this.socket.id);
			});

			this.socket.on('notification', (payload) => {
				console.log('New Notification:', payload);

				// New campaign lead — the server only sends this to admins. Shows the
				// in-page toast + sound and the native browser push, whatever tab is
				// active, like every other notification.
				if (payload?.type === NOTIFICATION_TYPES.LEAD_CREATED) {
					const meta = payload.metadata || {};

					store.dispatch(
						addAdminLeadAlert({
							_id: meta.leadId,
							intID: meta.intID,
							leadName: meta.leadName,
							source: meta.source,
						}),
					);
					store.dispatch(setNotifyItem());

					triggerBrowserNotification({
						title: payload.title || 'New Lead',
						message: payload.message,
						tag: `lead-created-${meta.leadId}`,
						duration: 15000,
						onClick: () => {
							// BrowserRouter listens to popstate, so this opens the
							// lead without a full page reload.
							window.history.pushState({}, '', leadUrl(meta.leadId));
							window.dispatchEvent(new PopStateEvent('popstate'));
						},
					});
					return;
				}

				triggerBrowserNotification({
					title: payload?.title || 'Notification',
					message: payload?.title || 'You received a new notification',
				});

				store.dispatch(setNotifyItem());

				if (payload?.type === NOTIFICATION_TYPES.ANNOUNCEMENT) {
					store.dispatch(addAnnouncement(payload));
				}
			});

			this.socket.on('unread_notifications', (payload) => {
				console.log('Unread Notifications:', payload);

				store.dispatch(addAnnouncements(payload));
				// if (payload?.type === NOTIFICATION_TYPES.ANNOUNCEMENT) {
				// 	store.dispatch(addAnnouncement(payload));
				// }
			});

			// this.socket.on('campaign:progress', (payload) => {
			// 	console.log('Campaign progress socket payload:', payload);
			// 	store.dispatch(updateCampaign(payload));
			// });

			this.socket.on('chatMessage', (msg) => {
				console.log('Message received:', msg);
				if (msg.roomId) {
					store.dispatch(
						appendMessage({
							chatId: msg.roomId,
							message: msg,
						}),
					);
				}
			});

			// Lead evnts
			this.socket.on('freshLead', (lead) => {
				console.log('Fresh Lead:', lead);
				// store.dispatch(setFreshLead(lead));
				store.dispatch(addFreshLead(lead));
			});

			this.socket.on('leadClaimed', (payload) => {
				console.log('Lead Claimed:', payload);
				// store.dispatch(setLeadClaimed(payload));
				store.dispatch(markLeadClaimed(payload));
			});

			this.socket.on('leadApprovalRequest', (payload) => {
				console.log('Lead Approval Request:', payload);
				store.dispatch(addFreshLeadPool(payload));
			});

			this.socket.on('leadDecisionFinalized', (payload) => {
				console.log('leadDecisionFinalized:', payload);
				store.dispatch(markLeadDecisionFinalized(payload));
			});

			this.socket.on('leadApproved', (payload) => {
				console.log('leadApproved:', payload);
				store.dispatch(addApprovalLead(payload));
			});

			this.socket.on('newContact', (contact) => {
				if (contact) {
					store.dispatch(addContact(contact));
				}
			});

			// An admin changed THIS user's role or custom permission override. The
			// permission map was a login-time snapshot; re-read the resolved
			// permissions so the sidebar and every `hasPermission` gate reflect the
			// change without a re-login. `force` skips the 30s throttle.
			// A plan change (`scope: 'plan'`) reaches the whole workspace at once, so
			// each browser waits a random 0–5 s: the re-reads must not land together.
			this.socket.on('permissions:changed', (payload) => {
				console.log('Permissions changed:', payload);
				const refresh = () => store.dispatch(refreshPermissions({ force: true }));
				if (payload?.scope === 'plan') setTimeout(refresh, Math.random() * 5000);
				else refresh();
			});

			this.socket.on('user_online', (data) => {
				console.log('User online:', data);
				store.dispatch(
					updateAllUsers({ id: data.userId, updates: { isOnline: true } }),
				);
				store.dispatch(setOnlineUsers(data));
			});

			this.socket.on('user_offline', (data) => {
				console.log('User offline:', data);
				store.dispatch(
					updateAllUsers({ id: data.userId, updates: { isOnline: false } }),
				);

				store.dispatch(setOnlineUsers(data));
			});

			this.socket.on('connection_status', (data) => {
				console.log('Connection Status:', data);
			});

			this.socket.on('activity_log_created', (data) => {
				console.log('Activity: ', data);
			});

			this.socket.on('qr', (data) => {
				console.log('QR data detalls: ', data);
			});

			// Connection error
			this.socket.on('connect_error', (error) => {
				this.connectionStatus = 'error';
				console.error('Socket connection error:', error);
				reject(error);
			});

			// Disconnection
			this.socket.on('disconnect', (reason) => {
				this.connectionStatus = 'disconnected';
				console.log('Socket disconnected:', reason);
				if (
					reason === 'io server disconnect' ||
					reason === 'ping timeout' ||
					reason === 'transport close'
				) {
					this.connectionPromise = null;
				}
			});
		});

		return this.connectionPromise;
	}

	/**
	 * Register user with the server
	 *  Registration payload
	 */
	registerUser(payload) {
		console.log({ socket: this.socket?.connected });
		if (this.socket?.connected) {
			this.socket.emit('register', payload);
		} else {
			if (!this.connectionPromise) {
				console.warn(
					'Socket not connected, registration will be attempted when connection is established',
				);
			}
		}
	}
	/**
	 * Register user with the server
	 *  Registration payload
	 */
	registerWhatsappUser(payload) {
		if (this.socket?.connected) {
			this.socket.emit('whatsapp_register', payload);
		} else {
			if (!this.connectionPromise) {
				console.warn(
					'Socket not connected, whatsapp registration will be attempted when connection is established',
				);
			}
		}
	}

	/**
	 * Register user with the server
	 *  Registration payload
	 */
	createUserActivityLog(payload) {
		if (this.socket?.connected) {
			this.socket.emit('user_activity', payload);
		} else {
			if (!this.connectionPromise) {
				console.warn(
					'Socket not connected, user activity log will be attempted when connection is established',
				);
			}
		}
	}

	/**
	 * Register user with the server
	 *  Registration payload
	 */
	buyFreshLead(payload) {
		if (this.socket?.connected) {
			this.socket.emit('buyLead', payload);
		} else {
			if (!this.connectionPromise) {
				console.warn(
					'Socket not connected, fresh lead purchase will be attempted when connection is established',
				);
			}
		}
	}

	/**
	 * Disconnect socket
	 */
	disconnect() {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
			this.connectionPromise = null;
			this.connectionStatus = 'disconnected';
			// this.reconnectionAttempts = 0;
		}
	}

	/**
	 * Register event listener
	 * @param {string} event - Event name
	 * @param {function} callback - Callback function
	 */
	on(event, handler) {
		// ★ THIS WAS WRAPPED IN `if ((this.connectionStatus = 'connected'))`.
		// A single `=`: it ASSIGNED 'connected' and then evaluated the assigned
		// string, which is always truthy — so it never gated anything, and every
		// call quietly reported the service as connected whether it was or not.
		// Removing the wrapper leaves registration exactly as it behaved; it
		// only stops `connectionStatus` being rewritten by a subscribe.
		//
		// It is deliberately NOT "fixed" to `===`: handlers are registered
		// before the handshake completes all over this app, and gating on the
		// real status would silently drop every one of them.
		if (!this.events.has(event)) this.events.set(event, []);
		this.events.get(event).push(handler);
		// May be null — see `_bindEvents`, which replays these on connect.
		if (this.socket) this.socket.on(event, handler);
	}

	/**
	 * Unregister event listener
	 * @param {string} event - Event name
	 * @param {function} handler - (optional) specific handler to remove
	 */
	off(event, handler) {
		if (!this.events.has(event)) return;

		if (handler) {
			// Remove only this handler
			const handlers = this.events.get(event).filter((h) => h !== handler);
			this.events.set(event, handlers);

			if (this.socket) {
				this.socket.off(event, handler);
			}
		} else {
			// Remove all handlers for this event

			const handlers = this.events.get(event);
			this.events.delete(event);

			if (this.socket) {
				for (const h of handlers) {
					this.socket.off(event, h);
				}
			}
		}
	}

	/**
	 * Internal: bind all registered events to socket
	 */
	_bindEvents() {
		if (!this.socket) return;
		for (const [event, handlers] of this.events.entries()) {
			for (const h of handlers) {
				// ★ OFF-THEN-ON, BECAUSE 'connect' FIRES ON EVERY RECONNECT.
				// socket.io keeps duplicate registrations of the same function, so
				// a plain `on` here would deliver each event twice after the first
				// reconnect, three times after the second. `off` with the same
				// reference is a no-op when it was never bound.
				this.socket.off(event, h);
				this.socket.on(event, h);
			}
		}
	}

	/**
	 * Emit an event to the server
	 * @param {string} event - Event name
	 * @param {*} data - Data to send
	 * @param {function} [ack] - Acknowledgement callback
	 * @returns {Promise} Resolves when acknowledged if no callback provided
	 */
	emit(event, data, ack) {
		if (!this.socket || !this.socket.connected) {
			// return Promise.reject(new Error('Socket not connected'));
			console.warn('Socket not connected!');
			return -1;
		}

		if (ack) {
			return this.socket.emit(event, data, ack);
		}

		return new Promise((resolve, reject) => {
			this.socket.emit(event, data, (response) => {
				if (response.error) {
					reject(response.error);
				} else {
					resolve(response);
				}
			});
		});
	}

	/**
	 * Get current connection status
	 * @returns {string} Connection status
	 */
	getConnectionStatus() {
		return this.connectionStatus;
	}

	/**
	 * Get socket instance
	 * @returns {Socket|null} Socket instance
	 */
	getSocket() {
		return this.socket;
	}
}

const socketService = new SocketService();

export default socketService;
